import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { toast } from 'react-toastify'; // Import toast for notifications
import { Button } from '../common/Button'; // Import the common Button
import { presetService } from '../../services/presetService'; // Import service
import { tradeSetService } from '../../services/tradeSetService'; // Re-added named import
import { positionService, Position } from '../../services/positionService'; // Import position service and type
import { orderService } from '../../services/orderService'; // Import orderService
import { infoService, SymbolPrecisionsResponse } from '../../services/infoService'; // Import infoService and type
import { Preset, TradeSet } from '../../types/preset'; // Import Preset and TradeSet types
import { OpenOrder, OrderHistoryEntry } from '../../types/order'; // Import order types
import OrderForm from './OrderForm'; // Import the new OrderForm
import EditTradeSetModal from '../tradeset/EditTradeSetModal'; // Import the modal
import OpenOrdersTable from './OpenOrdersTable'; // Import the new table component
import OrderHistoryTable from './OrderHistoryTable'; // Import the new table component
import { websocketService } from '../../services/websocket';
import { PlusIcon } from '@heroicons/react/24/solid'; // Import PlusIcon
import Wizard from '../preset/Wizard'; // Import Wizard component

// Add selectedSymbol to props
interface TradePanelProps {
  selectedSymbol: string;
}

// Styled Components
const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-left: 1px solid ${({ theme }) => theme.colors.divider};
  transition: width 0.3s ease;
  position: relative; /* Create new stacking context */
  z-index: 20; /* Ensure it's above the main content area */
`;

// Add Header Style
const PanelHeader = styled.div`
  padding: 0.8rem 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  justify-content: space-between; // To align title and potential future buttons
  align-items: center;
`;

const TabContainer = styled.div`
  display: flex;
  align-items: center; // Vertically align tabs and button
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Tab = styled.div<{ active: boolean }>`
  padding: 0.7rem 1rem;
  cursor: pointer;
  color: ${({ theme, active }) => (active ? theme.colors.text.primary : theme.colors.text.secondary)};
  border-bottom: 2px solid ${({ theme, active }) => (active ? theme.colors.primary.main : 'transparent')};
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background-color: ${({ theme }) => theme.colors.background.paper};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto; // Keep scrolling at the parent level
  padding: 1rem;
  gap: 1rem;
  min-height: 0; // Important for flexbox nested scrolling
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
`;

const SectionTitle = styled.h3`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  min-height: 100px; // Give sections a minimum height
  flex: 1; // Allow sections to grow equally
  
  &:last-child {
    border-bottom: none;
  }
`;

// Style for the preset list items - Enhanced for drag and drop
const ListItem = styled.div<{ $isActive: boolean; $isDragOver?: boolean }>`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.shape.borderRadius.small};
  margin-bottom: 4px;
  background-color: ${({ theme, $isActive, $isDragOver }) => {
    if ($isDragOver) return theme.colors.primary.light + '40'; // Drag over state
    if ($isActive) return theme.colors.primary.dark;
    return 'transparent';
  }};
  border: ${({ $isDragOver }) => $isDragOver ? '2px dashed #3b82f6' : '2px solid transparent'};
  transition: all 0.15s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary.dark : theme.colors.background.paper)};
  }
`;

// NEW: Draggable Preset Item
const DraggablePresetItem = styled(ListItem).attrs<{ $isDragging?: boolean }>({
  draggable: true
})<{ $isDragging?: boolean }>`
  opacity: ${({ $isDragging }) => $isDragging ? 0.5 : 1};
  transform: ${({ $isDragging }) => $isDragging ? 'scale(0.95)' : 'scale(1)'};
  
  &:hover {
    cursor: grab;
  }
  
  &:active {
    cursor: grabbing;
  }
`;

// NEW: Drop Zone for TradeSets
const TradeSetDropZone = styled(ListItem)<{ $isDropTarget?: boolean }>`
  border: ${({ $isDropTarget }) => $isDropTarget ? '2px dashed #10b981' : '2px solid transparent'};
  background-color: ${({ theme, $isDropTarget, $isActive }) => {
    if ($isDropTarget) return '#10b98120';
    if ($isActive) return theme.colors.primary.dark;
    return 'transparent';
  }};
  
  &::after {
    content: ${({ $isDropTarget }) => $isDropTarget ? '"Drop preset here to link"' : 'none'};
    position: absolute;
    bottom: -1.5rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.75rem;
    color: #10b981;
    font-weight: 500;
    white-space: nowrap;
  }
  
  position: relative;
`;

// NEW: Section with Drop Zone Support
const DroppableSection = styled(Section)<{ $isDragOver?: boolean }>`
  background-color: ${({ $isDragOver }) => $isDragOver ? '#f0f9ff' : 'transparent'};
  border: ${({ $isDragOver }) => $isDragOver ? '2px dashed #3b82f6' : 'none'};
  transition: all 0.2s ease;
`;

// NEW: Badge for preset links
const PresetLinkBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  font-size: 0.625rem;
  font-weight: 600;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  color: white;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
`;

// NEW: Connected preset indicator
const ConnectedPresetIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.5rem;
`;

// NEW: TradeSet color indicators
const TradeSetColors = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#84cc16', // Lime
];

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  padding: 0.25rem;
  margin-right: 0.5rem;
  color: #9ca3af;
  cursor: grab;
  
  &:hover {
    color: #6b7280;
  }
  
  &:active {
    cursor: grabbing;
  }
`;

// NEW: Visual feedback for drag operations
const DragPreview = styled.div`
  position: fixed;
  top: -1000px;
  left: -1000px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  pointer-events: none;
`;

const ListItemName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListItemActions = styled.div`
  display: flex;
  gap: 0.5rem;
  opacity: 0.7;
  
  ${ListItem}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  border-radius: ${({ theme }) => theme.shape.borderRadius.small};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.background.paper};
  }
`;

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: 1rem 0;
  text-align: center;
  font-size: 0.9rem;
`;

const LoadingState = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: 1rem 0;
  text-align: center;
  font-size: 0.9rem;
`;

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const TabTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// For badge indicators
const BadgeCount = styled.span`
  background: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: 0.7rem;
  padding: 0.1rem 0.5rem;
  border-radius: 9999px;
  margin-left: 0.5rem;
`;

// Style for the Create Preset Button
const CreatePresetButton = styled.button`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  font-size: 0.9rem;
  margin-left: auto; // Pushes the button to the right
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

// Add Modal styled component
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius.large};
  padding: 1.5rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.25rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// Add InlinePanelContent styled component for the wizard
const InlinePanelContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 1rem;
`;

const InlinePanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  padding-bottom: 1rem;
`;

const InlinePanelTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 1.25rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary.main};
  font-size: 0.9rem;
  padding: 0.5rem;
  cursor: pointer;
  margin-bottom: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Compact Step Indicator styles for inline wizard
const CompactStepContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem 0;
  overflow-x: auto;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CompactStep = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CompactStepDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background-color: ${({ theme, active, completed }) => {
    if (completed) return theme.colors.success.main;
    if (active) return theme.colors.primary.main;
    return theme.colors.background.paper;
  }};
`;

const CompactStepLabel = styled.span<{ active: boolean; completed: boolean }>`
  font-size: 0.8rem;
  color: ${({ theme, active, completed }) => {
    if (completed) return theme.colors.success.main;
    if (active) return theme.colors.primary.main;
    return theme.colors.text.secondary;
  }};
  white-space: nowrap;
`;

// Add InlineWizardContainer styled component
const InlineWizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
`;

const RefreshButton = styled.button`
  padding: ${({ theme }) => theme.spacing(0.5)} ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme }) => theme.colors.background.paper};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.shape.borderRadius.small};
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const ClassicalFormContainer = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  flex: 1;
  overflow: auto;
`;

/**
 * Trade Panel Component
 * 
 * This component handles all trade-related functionality including:
 * - Order execution with the OrderForm
 * - Preset management and execution with drag-and-drop linking
 * - TradeSet management and execution
 * - Visual relationship indicators between presets and TradeSets
 * - Order and position management
 * 
 * Recent updates:
 * - Added visual indicators for preset-TradeSet relationships
 * - Enhanced drag-and-drop with color-coded connections
 * - Quick TradeSet creation with + button
 */
const TradePanelComponent: React.FC<TradePanelProps> = ({ selectedSymbol }) => { // Destructure selectedSymbol prop
  const navigate = useNavigate(); // Get navigate function
  
  // State for data
  const [presets, setPresets] = useState<Preset[]>([]);
  const [tradeSets, setTradeSets] = useState<TradeSet[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(true);
  const [loadingTradeSets, setLoadingTradeSets] = useState(true);
  const [errorPresets, setErrorPresets] = useState<string | null>(null);
  const [errorTradeSets, setErrorTradeSets] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [selectedTradeSet, setSelectedTradeSet] = useState<TradeSet | null>(null);
  const [loadingExecution, setLoadingExecution] = useState(false); // Add loading state for execution

  // NEW: Drag and Drop State
  const [draggedPreset, setDraggedPreset] = useState<Preset | null>(null);
  const [dragOverTradeSet, setDragOverTradeSet] = useState<string | null>(null);
  const [isDraggingOverSection, setIsDraggingOverSection] = useState(false);
  const [expandedTradeSetId, setExpandedTradeSetId] = useState<string | null>(null);
  
  // State for Active Tab data
  const [positions, setPositions] = useState<Position[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [errorPositions, setErrorPositions] = useState<string | null>(null);

  // State for Open Orders
  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]); // Use correct type
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [errorOrders, setErrorOrders] = useState<string | null>(null);

  // State for Order History
  const [orderHistory, setOrderHistory] = useState<OrderHistoryEntry[]>([]); // Use correct type
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorHistory, setErrorHistory] = useState<string | null>(null);
  
  // Tab state - Updated tab names to match Phase 2 requirements
  const [activeTab, setActiveTab] = useState<'monitor' | 'presets' | 'classical'>('monitor');
  
  // State for Edit Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTradeSet, setEditingTradeSet] = useState<TradeSet | null>(null);

  // State for Symbol Precisions
  const [symbolPrecisions, setSymbolPrecisions] = useState<SymbolPrecisionsResponse>({});
  const [loadingPrecisions, setLoadingPrecisions] = useState(true);
  const [errorPrecisions, setErrorPrecisions] = useState<string | null>(null);

  // State for cancellation status
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);

  // State for WebSocket connection
  const [wsConnected, setWsConnected] = useState(false);
  const [presetExecutionUpdates, setPresetExecutionUpdates] = useState<Record<string, any>>({});
  const [marketData, setMarketData] = useState<Record<string, any>>({});

  // Add state for wizard modal
  const [isCreatingPreset, setIsCreatingPreset] = useState(false);
  const [isWizardModalOpen, setIsWizardModalOpen] = useState(false);
  const [isCreatingTradeSet, setIsCreatingTradeSet] = useState(false);
  const [isEditingPreset, setIsEditingPreset] = useState(false);
  const [editingPresetData, setEditingPresetData] = useState<Preset | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const originalWidthRef = useRef<string>('');

  // Define fetch functions outside useEffect so they can be called from other places
  const fetchPresets = async () => {
    try {
      setLoadingPresets(true);
      console.log('🔄 Fetching presets...');
      // Fetch a reasonable number, pagination not needed for this list view
      const response = await presetService.getPresets(1, 50, undefined, selectedSymbol);
      console.log('✅ Presets fetched:', response.items.length, 'items');
      console.log('📋 All presets:', response.items.map(p => ({ name: p.name, symbol: p.symbol, id: p.id })));
      setPresets(response.items);
      setErrorPresets(null);
    } catch (err) {
      console.error('❌ Error fetching presets for panel:', err);
      setErrorPresets('Failed to load presets.');
    } finally {
      setLoadingPresets(false);
    }
  };

  const fetchTradeSets = async () => {
    try {
      setLoadingTradeSets(true);
      console.log('🔄 Fetching tradeSets...');
      // Fetch tradeSets with a reasonable limit
      const response = await tradeSetService.getTradeSets(1, 50, selectedSymbol);
      console.log('✅ TradeSets fetched:', response.items.length, 'items');
      setTradeSets(response.items);
      setErrorTradeSets(null);
    } catch (err) {
      console.error('❌ Error fetching tradeSets for panel:', err);
      setErrorTradeSets('Failed to load tradeSets.');
    } finally {
      setLoadingTradeSets(false);
    }
  };

  // Fetch presets on component mount and when symbol changes
  useEffect(() => {
    fetchPresets();
  }, [selectedSymbol]);
  
  // Fetch tradeSets on component mount and when symbol changes
  useEffect(() => {
    fetchTradeSets();
  }, [selectedSymbol]);
  
  // --- Data fetching for Active Tab --- 
  const fetchActiveData = useCallback(async () => {
    // Reset states
    setLoadingPositions(true);
    setErrorPositions(null);
    setLoadingOrders(true);
    setErrorOrders(null);
    setLoadingHistory(true);
    setErrorHistory(null);
    setLoadingPrecisions(true); // Reset precision loading state
    setErrorPrecisions(null);

    try {
      // Use Promise.allSettled to fetch concurrently and handle individual errors
      const results = await Promise.allSettled([
        positionService.getPositions(),
        orderService.getOpenOrders(),
        orderService.getOrderHistory(), // Add limit/symbol params if needed later
        infoService.getSymbolPrecisions() // Fetch precision data concurrently
      ]);

      // Process Position results (index 0)
      if (results[0].status === 'fulfilled') {
        setPositions(results[0].value);
      } else {
        console.error('Error fetching positions for Monitor tab:', results[0].reason);
        setErrorPositions('Failed to load positions.');
        setPositions([]);
      }
      setLoadingPositions(false);

      // Process Open Orders results (index 1)
      if (results[1].status === 'fulfilled') {
        setOpenOrders(results[1].value);
      } else {
        console.error('Error fetching open orders for Monitor tab:', results[1].reason);
        setErrorOrders('Failed to load open orders.');
        setOpenOrders([]);
      }
      setLoadingOrders(false);

      // Process Order History results (index 2)
      if (results[2].status === 'fulfilled') {
        setOrderHistory(results[2].value);
      } else {
        console.error('Error fetching order history for Monitor tab:', results[2].reason);
        setErrorHistory('Failed to load order history.');
        setOrderHistory([]);
      }
      setLoadingHistory(false);

      // Process Symbol Precisions results (index 3)
      if (results[3].status === 'fulfilled') {
        setSymbolPrecisions(results[3].value);
      } else {
        console.error('Error fetching symbol precisions for Monitor tab:', results[3].reason);
        setErrorPrecisions('Failed to load symbol precision data.');
        setSymbolPrecisions({}); // Reset to empty on error
      }
      setLoadingPrecisions(false);

    } catch (err: any) {
      console.error('Unexpected error fetching Monitor tab data:', err);
      // Set generic error states if something goes wrong with Promise.allSettled
      setErrorPositions('Failed to load Monitor tab data.');
      setErrorOrders('Failed to load Monitor tab data.');
      setErrorHistory('Failed to load Monitor tab data.');
      setErrorPrecisions('Failed to load Monitor tab data.');
      setLoadingPositions(false);
      setLoadingOrders(false);
      setLoadingHistory(false);
      setLoadingPrecisions(false);
    }
  }, []); // Add dependencies if needed, but likely none for initial fetch

  // Fetch data when the Monitor tab OR selectedSymbol changes
  useEffect(() => {
    if (activeTab === 'monitor') {
      fetchActiveData();
    }
    // Add fetch logic for other tabs if necessary
  }, [activeTab, fetchActiveData, selectedSymbol]); // Add selectedSymbol dependency
  
  // Filter presets by selectedSymbol prop
  const symbolFilteredPresets = presets.filter(
    preset => preset.symbol === selectedSymbol
  );
  
  // Filter tradeSets by selectedSymbol prop
  const symbolFilteredTradeSets = tradeSets.filter(
    tradeSet => tradeSet.symbol === selectedSymbol
  );
  
  // Debug logging for filtering
  useEffect(() => {
    console.log('🔍 Symbol filtering debug:', {
      selectedSymbol,
      totalPresets: presets.length,
      filteredPresets: symbolFilteredPresets.length,
      allPresetSymbols: presets.map(p => p.symbol),
      filteredPresetNames: symbolFilteredPresets.map(p => p.name)
    });
  }, [presets, selectedSymbol, symbolFilteredPresets]);
  
  // Phase 2.3: Enhanced symbol change handling
  useEffect(() => {
    console.log(`TradePanel: Symbol changed to ${selectedSymbol}, filtering data...`);
    
    // Refresh data when symbol changes to ensure we have latest data for the new symbol
    if (activeTab === 'monitor') {
      fetchActiveData();
    }
    
    // Update WebSocket subscriptions for the new symbol
    if (wsConnected) {
      websocketService.connectToSymbol(selectedSymbol);
    }
  }, [selectedSymbol, activeTab, wsConnected]);

  // Modified handleNewPreset function to use inline panel rather than modal
  const handleNewPreset = () => {
    // Save the original width before expanding
    if (panelRef.current) {
      originalWidthRef.current = panelRef.current.style.width || '';
      panelRef.current.style.width = '550px'; // Expand panel for wizard
    }
    setIsCreatingPreset(true);
  };

  // Function to exit preset creation and restore original panel width
  const handleExitPresetCreation = (shouldRefresh = false) => {
    // Restore original width
    if (panelRef.current && originalWidthRef.current) {
      panelRef.current.style.width = originalWidthRef.current;
    }
    setIsCreatingPreset(false);
    if (shouldRefresh) {
      console.log('🔄 Refreshing presets and tradeSets after creation...');
      fetchPresets();
      fetchTradeSets();
    }
  };

  // Handle preset creation completion
  const handlePresetCreated = (newPreset: Preset) => {
    console.log('🎉 Preset created successfully:', newPreset);
    console.log('🔄 Triggering refresh...');
    handleExitPresetCreation(true);
    toast.success(`Preset "${newPreset.name}" created successfully`);
  };

  // Modified function to close modal (keep for compatibility)
  const handleCloseWizardModal = () => {
    setIsWizardModalOpen(false);
  };

  const handleEditPresetClick = (preset: Preset) => {
    setEditingPresetData(preset);
    setIsEditingPreset(true);
  };

  const handleExitPresetEdit = () => {
    setIsEditingPreset(false);
    setEditingPresetData(null);
  };

  const handlePresetUpdated = (updatedPreset: Preset | null) => {
    console.log('Preset updated/deleted:', updatedPreset);
    // Refresh the presets list
    fetchPresets();
    // Exit edit mode
    handleExitPresetEdit();
    // Show success message only for updates (not deletes)
    if (updatedPreset && updatedPreset.name) {
      toast.success(`PreSet "${updatedPreset.name}" updated successfully!`);
    }
    // Delete success message is already shown in the Wizard component
  };

  // --- Edit Modal Handlers ---
  const handleEditTradeSetClick = (tradeSet: TradeSet) => {
    setEditingTradeSet(tradeSet);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTradeSet(null);
  };

  const handleTradeSetUpdated = (updatedTradeSet: TradeSet) => {
    setTradeSets(prev =>
      prev.map(ts => (ts.id === updatedTradeSet.id ? updatedTradeSet : ts))
    );
    toast.success(`TradeSet '${updatedTradeSet.name}' updated.`); // Add toast notification
  };

  // Handler for preset execution
  const handleExecutePreset = async (preset: Preset) => {
    setLoadingExecution(true); // Set loading state
    try {
      console.log('🔄 Starting preset execution:', {
        presetId: preset.id,
        presetName: preset.name,
        symbol: preset.symbol,
        orderType: preset.order_type,
        side: preset.side,
        amount: preset.amount,
        amountType: preset.amount_type
      });
      
      // Convert preset.id to string for the service call
      const result = await presetService.executePreset(preset.id.toString()); 
      
      console.log('✅ Preset execution successful:', result);
      
      // Use a more accurate "submitted" message, as the final fill comes via WebSocket
      toast.info(`Preset '${preset.name}' submitted successfully. Awaiting confirmation.`);
      setSelectedPreset(null); // Deselect after execution
      
      // The fetchActiveData is now less critical as WebSocket will provide the update
      // We can keep it as a fallback.
      if (activeTab === 'monitor') {
        setTimeout(() => {
          fetchActiveData();
        }, 2000); // Keep delay for fallback
      }
    } catch (err: any) {
      console.error('❌ Error executing preset:', err);
      toast.error(`Error executing preset '${preset.name}': ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingExecution(false); // Clear loading state
    }
  };
  
  // Handler for preset selection
  const handlePresetSelect = (preset: Preset) => {
    console.log('Selected preset:', preset.name);
    setSelectedPreset(preset);
    setSelectedTradeSet(null);
    // Potentially show details or enable execution button
  };
  
  // Handler for tradeSet execution
  const handleExecuteTradeSet = async (tradeSet: TradeSet) => {
    setLoadingExecution(true); // Set loading state
    // This might involve executing multiple presets sequentially or in parallel
    // For now, let's assume it triggers a specific backend endpoint if available
    // Or simply logs an action, as TradeSet execution isn't fully defined yet
    console.log('Executing TradeSet:', tradeSet.name);
    toast.info(`Execution for TradeSet '${tradeSet.name}' not yet implemented.`);
    // try {
    //   await tradeSetService.executeTradeSet(tradeSet.id);
    //   toast.success(`TradeSet '${tradeSet.name}' execution started.`);
    //   // Refresh data if needed
    //   if (activeTab === 'monitor') {
    //     fetchActiveData();
    //   }
    // } catch (err: any) {
    //   console.error('Error executing tradeSet:', err);
    //   toast.error(`Error executing TradeSet '${tradeSet.name}': ${err.message || 'Unknown error'}`);
    // } finally {
    //   setLoadingExecution(false); // Clear loading state
    // }
    setSelectedTradeSet(null); // Deselect
    setLoadingExecution(false); // Clear loading state (remove if using try/finally)
  };
  
  // Handler for tradeSet selection
  const handleTradeSetSelect = (tradeSet: TradeSet) => {
    console.log('Selected tradeSet:', tradeSet.name);
    setSelectedTradeSet(tradeSet);
    setSelectedPreset(null);
    setExpandedTradeSetId(prev => prev === tradeSet.id ? null : tradeSet.id);
  };

  // Handler for creating an empty TradeSet
  const handleCreateEmptyTradeSet = () => {
    // Open inline wizard preselected to TradeSet flow
    if (panelRef.current) {
      originalWidthRef.current = panelRef.current.style.width || '';
      panelRef.current.style.width = '550px';
    }
    setIsCreatingTradeSet(true);
  };
  
  // --- Refresh Handler --- 
  const handleRefreshActiveData = () => {
    toast.info('Refreshing active data...');
    fetchActiveData();
  };

  // Handler for cancelling an order
  const handleCancelOrder = async (symbol: string, orderId: string) => {
    if (!orderId) {
      toast.error("Cannot cancel order: Missing Order ID.");
      return;
    }
    setCancellingOrderId(orderId);
    try {
      const result = await orderService.cancelOrder(symbol, orderId);
      toast.success(`Order ${orderId} cancelled successfully.`);
      // Refresh data to reflect the cancellation
      fetchActiveData(); 
      // Alternatively, optimistically remove the order from state:
      // setOpenOrders(prevOrders => prevOrders.filter(o => o.order_id !== orderId));
    } catch (err: any) {
      console.error(`Error cancelling order ${orderId}:`, err);
      toast.error(`Failed to cancel order ${orderId}: ${err.message || 'Unknown error'}`);
    } finally {
      setCancellingOrderId(null);
    }
  };

  // --- WebSocket Setup --- 
  useEffect(() => {
    // Define WebSocket event handlers
    const handleOrderUpdate = (orderData: any) => {
      console.log('Order update received in Trade Panel:', orderData);
      
      // Refresh orders if we're on the Monitor tab
      if (activeTab === 'monitor') {
        fetchActiveData();
      }
    };

    const handlePositionUpdate = (positionData: any) => {
      console.log('Position update received in Trade Panel:', positionData);
      
      // Update positions state directly for real-time updates
      setPositions(prevPositions => {
        const updatedPositions = [...prevPositions];
        const index = updatedPositions.findIndex(p => p.coin === positionData.coin);
        
        if (index >= 0) {
          // Update existing position
          updatedPositions[index] = { ...updatedPositions[index], ...positionData };
        } else if (positionData.size > 0) {
          // Add new position if size > 0
          updatedPositions.push(positionData);
        }
        
        return updatedPositions.filter(p => p.size > 0); // Remove positions with 0 size
      });
    };

    // Phase 2: Preset execution monitoring WebSocket handlers
    const handlePresetExecutionUpdate = (executionData: any) => {
      console.log('Preset execution update received:', executionData);
      
      setPresetExecutionUpdates(prev => ({
        ...prev,
        [executionData.data.preset_id]: executionData.data
      }));
      
      // Show toast notifications for important status changes
      const { status, preset_id } = executionData.data;
      switch (status) {
        case 'started':
          toast.info(`Preset execution started for ${preset_id}`);
          break;
        case 'completed':
          toast.success(`Preset execution completed for ${preset_id}`);
          break;
        case 'failed':
          toast.error(`Preset execution failed for ${preset_id}`);
          break;
      }
    };

    // Phase 2: Market data updates for symbol-specific calculations
    const handleMarketDataUpdate = (marketDataUpdate: any) => {
      console.log('Market data update received:', marketDataUpdate);
      
      setMarketData(prev => ({
        ...prev,
        [marketDataUpdate.symbol]: marketDataUpdate
      }));
    };

    const handleConnection = () => {
      console.log('WebSocket connected in Trade Panel');
      setWsConnected(true);
      
      // Subscribe to market data for the currently selected symbol
      if (selectedSymbol) {
        websocketService.subscribeToMarketData(selectedSymbol);
      }
    };

    const handleDisconnection = () => {
      console.log('WebSocket disconnected in Trade Panel');
      setWsConnected(false);
    };

    // Register event listeners with correct event names
    websocketService.on('orderUpdate', handleOrderUpdate);
    websocketService.on('positionUpdate', handlePositionUpdate);
    websocketService.on('marketDataUpdate', handleMarketDataUpdate);
    websocketService.on('presetExecutionUpdate', handlePresetExecutionUpdate);
    websocketService.on('connected', handleConnection);
    websocketService.on('disconnected', handleDisconnection);

    // Subscribe to preset monitoring
    websocketService.subscribeToPresetMonitor();

    // Initialize connection
    websocketService.connect();

    // Cleanup function
    return () => {
      websocketService.off('orderUpdate', handleOrderUpdate);
      websocketService.off('positionUpdate', handlePositionUpdate);
      websocketService.off('marketDataUpdate', handleMarketDataUpdate);
      websocketService.off('presetExecutionUpdate', handlePresetExecutionUpdate);
      websocketService.off('connected', handleConnection);
      websocketService.off('disconnected', handleDisconnection);
    };
  }, [activeTab, selectedSymbol]); // Add selectedSymbol to dependency array

  // Additional useEffect to handle symbol changes for market data subscriptions
  useEffect(() => {
    if (wsConnected && selectedSymbol) {
      // Subscribe to market data for the new symbol
      websocketService.subscribeToMarketData(selectedSymbol);
    }
  }, [selectedSymbol, wsConnected]); // Subscribe when symbol changes or connection is established

  // NEW: Drag and Drop Handlers
  const handlePresetDragStart = (e: React.DragEvent, preset: Preset) => {
    console.log('🔄 Drag started for preset:', preset.name);
    setDraggedPreset(preset);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'preset',
      preset: preset
    }));
    
    // Create drag preview
    const dragPreview = document.createElement('div');
    dragPreview.className = 'drag-preview';
    dragPreview.textContent = `Link "${preset.name}" to TradeSet`;
    dragPreview.style.cssText = `
      position: absolute;
      top: -1000px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 0.375rem;
      padding: 0.5rem 0.75rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      font-size: 0.875rem;
      color: #374151;
    `;
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);
    
    // Clean up preview after drag
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  const handlePresetDragEnd = () => {
    console.log('🔄 Drag ended');
    setDraggedPreset(null);
    setDragOverTradeSet(null);
    setIsDraggingOverSection(false);
  };

  const handleTradeSetDragOver = (e: React.DragEvent, tradeSetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverTradeSet(tradeSetId);
  };

  const handleTradeSetDragLeave = () => {
    setDragOverTradeSet(null);
  };

  const handleTradeSetDrop = async (e: React.DragEvent, targetTradeSet: TradeSet) => {
    e.preventDefault();
    setDragOverTradeSet(null);
    setIsDraggingOverSection(false);
    
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      
      if (dragData.type === 'preset' && draggedPreset) {
        console.log('🔄 Linking preset to TradeSet:', {
          preset: draggedPreset.name,
          tradeSet: targetTradeSet.name
        });
        
        // Check if preset symbol matches TradeSet symbol
        if (draggedPreset.symbol !== targetTradeSet.symbol) {
          toast.error(`Cannot link ${draggedPreset.name} (${draggedPreset.symbol}) to ${targetTradeSet.name} (${targetTradeSet.symbol}). Symbols must match.`);
          return;
        }
        
        // Check if preset is already linked to this TradeSet
        const isAlreadyLinked = targetTradeSet.preset_links?.some(p => p.preset_id === draggedPreset.id);
        if (isAlreadyLinked) {
          toast.warning(`"${draggedPreset.name}" is already linked to "${targetTradeSet.name}"`);
          return;
        }
        
        // Add preset to TradeSet - Fix: Use preset_links property correctly
        const maxOrder = targetTradeSet.preset_links?.length ? 
          Math.max(...targetTradeSet.preset_links.map(p => p.order)) : 0;
        
        const newPresetLink = {
          preset_id: draggedPreset.id,
          order: maxOrder + 1
        };
        
        const updatedPresetLinks = [...(targetTradeSet.preset_links || []), newPresetLink];
        
        // Fix: Call the service with the correct parameter structure
        await tradeSetService.updateTradeSetPresets(targetTradeSet.id, updatedPresetLinks);
        
        // Refresh TradeSets to get updated data
        await fetchTradeSets();
        
        toast.success(`Successfully linked "${draggedPreset.name}" to "${targetTradeSet.name}"`);
      }
    } catch (error) {
      console.error('Error linking preset to TradeSet:', error);
      toast.error('Failed to link preset to TradeSet');
    }
  };

  const handleSectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverSection(true);
  };

  const handleSectionDragLeave = (e: React.DragEvent) => {
    // Only set to false if we're actually leaving the section
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOverSection(false);
    }
  };

  // NEW: Helper functions for visual indicators
  const getTradeSetColor = (tradeSetId: string): string => {
    const index = symbolFilteredTradeSets.findIndex(ts => ts.id === tradeSetId);
    return TradeSetColors[index % TradeSetColors.length];
  };

  const getLinkedTradeSets = (presetId: string): TradeSet[] => {
    return symbolFilteredTradeSets.filter(ts => 
      ts.preset_links?.some(link => link.preset_id === presetId)
    );
  };

  const getPresetOrderInTradeSet = (presetId: string, tradeSetId: string): number | null => {
    const tradeSet = symbolFilteredTradeSets.find(ts => ts.id === tradeSetId);
    const link = tradeSet?.preset_links?.find(link => link.preset_id === presetId);
    return link?.order || null;
  };

  // Renders the main content based on the active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'monitor':
        return renderMonitorTab();
      case 'presets':
        return renderPresetsTab();
      case 'classical':
        return renderClassicalOrderTab();
      default:
        return null;
    }
  };
  
  // Monitor Tab Content (renamed from renderActiveTab)
  const renderMonitorTab = () => (
    <>
      {isCreatingPreset ? (
        <InlineWizardContainer>
          <BackButton onClick={() => handleExitPresetCreation()}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Trade Panel
          </BackButton>
          <Wizard 
            onPresetCreated={handlePresetCreated} 
            selectedSymbol={selectedSymbol} 
            isInModal={false} 
          />
        </InlineWizardContainer>
      ) : (
        <ContentArea>
          <TabHeader style={{ flexShrink: 0 }}>
            <TabTitle>preSet Monitor - {selectedSymbol}</TabTitle>
            <Button onClick={handleRefreshActiveData} variant="secondary" size="small">
              Refresh
            </Button>
          </TabHeader>

          {/* ACTIVE preSets Section - Flex: 1 to share available space */}
          <Section style={{ flex: 1 }}>
            <SectionTitle>ACTIVE preSets ({positions.length}) - {selectedSymbol}</SectionTitle>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {loadingPositions && <LoadingState>Loading active presets...</LoadingState>}
              {errorPositions && <EmptyState>{errorPositions}</EmptyState>}
              {!loadingPositions && !errorPositions && (
                positions.length > 0 ? (
                   <div>
                     <p>Active presets monitoring ready ({positions.length} positions)</p>
                     {/* Phase 2: Real-time preset status will be displayed here */}
                     {presetExecutionUpdates && Object.keys(presetExecutionUpdates).length > 0 && (
                       <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
                         WebSocket preset updates: {Object.keys(presetExecutionUpdates).length} active
                       </div>
                     )}
                     {marketData && Object.keys(marketData).length > 0 && (
                       <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#888' }}>
                         Market data: {Object.keys(marketData).join(', ')}
                       </div>
                     )}
                     {/* Phase 2.3: Symbol-specific preset filtering */}
                     {symbolFilteredPresets.length > 0 && (
                       <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#10b981' }}>
                         {symbolFilteredPresets.length} presets available for {selectedSymbol}
                       </div>
                     )}
                   </div>
                ) : (
                  <EmptyState>No active presets for {selectedSymbol}.</EmptyState>
                )
              )}
            </div>
          </Section>

          {/* Open Orders Section - Flex: 1 to share available space */}
          <Section style={{ flex: 1 }}>
            <SectionTitle>Open Orders ({openOrders.length})</SectionTitle>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {(loadingOrders || loadingPrecisions) && <LoadingState>Loading open orders...</LoadingState>}
              {errorOrders && <EmptyState>{errorOrders}</EmptyState>}
              {errorPrecisions && <EmptyState>Error loading symbol precisions.</EmptyState>}
              {!loadingOrders && !loadingPrecisions && !errorOrders && !errorPrecisions && (
                <OpenOrdersTable 
                   orders={openOrders} 
                   symbolPrecisions={symbolPrecisions}
                   onCancelOrder={handleCancelOrder}
                 />
              )}
            </div>
          </Section>

          {/* Order History Section - Flex: 1 to share available space */}
          <Section style={{ flex: 1 }}>
            <SectionTitle>Order History</SectionTitle>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {(loadingHistory || loadingPrecisions) && <LoadingState>Loading order history...</LoadingState>}
              {errorHistory && <EmptyState>{errorHistory}</EmptyState>}
              {errorPrecisions && <EmptyState>Error loading symbol precisions.</EmptyState>}
              {!loadingHistory && !loadingPrecisions && !errorHistory && !errorPrecisions && (
                <OrderHistoryTable 
                   history={orderHistory} 
                   symbolPrecisions={symbolPrecisions}
                 />
              )}
            </div>
          </Section>
        </ContentArea>
      )}
    </>
  );
  
  // Presets Tab Content - Enhanced with visual relationship indicators
  const renderPresetsTab = () => (
    <>
      <TabHeader>
        <TabTitle>
          {selectedSymbol} PreSets & TradeSets
        </TabTitle>
        <Button 
          size="small" 
          onClick={handleNewPreset}
          style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
        >
          <PlusIcon style={{ width: '16px', height: '16px' }} /> New PreSet
        </Button>
      </TabHeader>
      
      <Section>
        <SectionTitle>preSets ({symbolFilteredPresets.length})</SectionTitle>
        <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
          <RefreshButton 
            onClick={() => {
              console.log('🔄 Manual refresh triggered');
              fetchPresets();
            }}
          >
            🔄 Refresh Presets
          </RefreshButton>
          Drag presets to TradeSets below to link them
        </div>
        {loadingPresets ? (
          <LoadingState>Loading presets...</LoadingState>
        ) : errorPresets ? (
          <EmptyState>{errorPresets}</EmptyState>
        ) : symbolFilteredPresets.length === 0 ? (
          <EmptyState>
            No presets for {selectedSymbol}. 
            {presets.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>
                Available symbols: {Array.from(new Set(presets.map(p => p.symbol))).join(', ')}
              </div>
            )}
          </EmptyState>
        ) : (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {symbolFilteredPresets.map((preset) => {
              const linkedTradeSets = getLinkedTradeSets(preset.id);
              
              return (
                <DraggablePresetItem
                  key={preset.id} 
                  $isActive={selectedPreset?.id === preset.id}
                  $isDragging={draggedPreset?.id === preset.id}
                  onClick={() => handlePresetSelect(preset)}
                  onDragStart={(e) => handlePresetDragStart(e, preset)}
                  onDragEnd={handlePresetDragEnd}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DragHandle>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                        <circle cx="3" cy="3" r="1"/>
                        <circle cx="3" cy="6" r="1"/>
                        <circle cx="3" cy="9" r="1"/>
                        <circle cx="9" cy="3" r="1"/>
                        <circle cx="9" cy="6" r="1"/>
                        <circle cx="9" cy="9" r="1"/>
                      </svg>
                    </DragHandle>
                    <ListItemName>{preset.name}</ListItemName>
                    {/* Visual indicators for linked TradeSets */}
                    {linkedTradeSets.length > 0 && (
                      <ConnectedPresetIndicator>
                        {linkedTradeSets.map((tradeSet) => {
                          const order = getPresetOrderInTradeSet(preset.id, tradeSet.id);
                          return (
                            <PresetLinkBadge
                              key={tradeSet.id}
                              $color={getTradeSetColor(tradeSet.id)}
                              title={`Linked to "${tradeSet.name}" (position #${order})`}
                            >
                              {order}
                            </PresetLinkBadge>
                          );
                        })}
                      </ConnectedPresetIndicator>
                    )}
                  </div>
                  <ListItemActions>
                    <ActionButton onClick={(e) => {
                      e.stopPropagation();
                      handleEditPresetClick(preset);
                    }}>
                      Edit
                    </ActionButton>
                    <ActionButton onClick={(e) => {
                      e.stopPropagation();
                      handleExecutePreset(preset);
                    }}>
                      Execute
                    </ActionButton>
                  </ListItemActions>
                </DraggablePresetItem>
              );
            })}
          </div>
        )}
      </Section>
      
      <DroppableSection 
        $isDragOver={isDraggingOverSection}
        onDragOver={handleSectionDragOver}
        onDragLeave={handleSectionDragLeave}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <SectionTitle>
            tradeSets ({symbolFilteredTradeSets.length})
            {draggedPreset && (
              <span style={{ fontSize: '0.75rem', color: '#10b981', marginLeft: '0.5rem' }}>
                Drop zones active
              </span>
            )}
          </SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <RefreshButton 
              onClick={() => {
                console.log('🔄 Manual tradeSets refresh');
                fetchTradeSets();
              }}
            >
              🔄 Refresh TradeSets
            </RefreshButton>
            <Button
            size="small"
            variant="secondary"
            onClick={handleCreateEmptyTradeSet}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.25rem',
              fontSize: '0.75rem',
              padding: '0.25rem 0.5rem'
            }}
            >
              <PlusIcon style={{ width: '12px', height: '12px' }} /> 
              New TradeSet
            </Button>
          </div>
        </div>
        {loadingTradeSets ? (
          <LoadingState>Loading tradeSets...</LoadingState>
        ) : errorTradeSets ? (
          <EmptyState>{errorTradeSets}</EmptyState>
        ) : symbolFilteredTradeSets.length === 0 ? (
          <EmptyState>No tradeSets for {selectedSymbol}.</EmptyState>
        ) : (
          <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
            {symbolFilteredTradeSets.map((tradeSet, index) => (
              <TradeSetDropZone
                key={tradeSet.id} 
                $isActive={selectedTradeSet?.id === tradeSet.id}
                $isDropTarget={dragOverTradeSet === tradeSet.id}
                onClick={() => handleTradeSetSelect(tradeSet)}
                onDragOver={(e) => handleTradeSetDragOver(e, tradeSet.id)}
                onDragLeave={handleTradeSetDragLeave}
                onDrop={(e) => handleTradeSetDrop(e, tradeSet)}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  {/* TradeSet color indicator */}
                  <div 
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: getTradeSetColor(tradeSet.id),
                      borderRadius: '50%',
                      marginRight: '0.5rem',
                      flexShrink: 0
                    }}
                  />
                  <ListItemName>
                    {tradeSet.name} 
                    {tradeSet.preset_links && tradeSet.preset_links.length > 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                        ({tradeSet.preset_links.length} presets)
                      </span>
                    )}
                  </ListItemName>
                </div>
                <ListItemActions>
                  <ActionButton onClick={(e) => {
                    e.stopPropagation();
                    handleEditTradeSetClick(tradeSet);
                  }}>
                    Edit
                  </ActionButton>
                  <ActionButton onClick={(e) => {
                    e.stopPropagation();
                    handleExecuteTradeSet(tradeSet);
                  }}>
                    Execute
                  </ActionButton>
                </ListItemActions>
              </TradeSetDropZone>
            ))}
          </div>
        )}
      </DroppableSection>

      {/* Inline expansion below list items (moved outside ternary to avoid JSX parse issues) */}
      {expandedTradeSetId && (
        <div style={{
          marginTop: '8px',
          padding: '8px',
          border: '1px solid #374151',
          borderRadius: '6px',
          background: '#111827'
        }}>
          {(() => {
            const ts = symbolFilteredTradeSets.find(t => t.id === expandedTradeSetId);
            if (!ts) return <EmptyState>TradeSet not found.</EmptyState>;
            const presetNames = (ts as any).presets as any[] | undefined;
            if (!presetNames || presetNames.length === 0) {
              return <EmptyState>No linked presets. Drag presets above to link them.</EmptyState>;
            }
            return (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {presetNames.map((p: any) => (
                  <button
                    key={p.id}
                    onClick={() => handleExecutePreset({ id: p.id, name: p.name, symbol: p.symbol } as any)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #374151',
                      background: '#1f2937',
                      color: '#e5e7eb'
                    }}
                    title={`Execute preset: ${p.name}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
  
  // Classical Order Form Tab - Potentially pass selectedSymbol down?
  const renderClassicalOrderTab = () => (
    <ContentArea>
      <TabHeader>
        <TabTitle>
          {selectedSymbol} Classical Trading
        </TabTitle>
      </TabHeader>
      
      <ClassicalFormContainer>
        <OrderForm 
          selectedPreset={selectedPreset}
          selectedSymbol={selectedSymbol}
          onOrderSubmitted={(success, message) => {
            console.log("Classical order submission:", success, message);
            if (success) {
              // Optionally clear the selected preset if it was used for defaults
              // setSelectedPreset(null);
              fetchActiveData(); // Refresh active data (orders, positions)
              toast.success(`Order ${message}`);
            } else {
              toast.error(`Order failed: ${message}`);
            }
            // You might want additional logic here, e.g., clearing the form on success
          }}
        />
      </ClassicalFormContainer>
    </ContentArea>
  );

  return (
    <PanelContainer ref={panelRef}>
      {/* Added Panel Header */}
      <PanelHeader>Trade Panel</PanelHeader>
      
      {!isCreatingPreset && !isEditingPreset && !isCreatingTradeSet ? (
        // Regular Trade Panel content
        <>
          <TabContainer>
            {/* Updated Tab Labels */}
            <Tab active={activeTab === 'monitor'} onClick={() => setActiveTab('monitor')}>
              Monitor 
              {/* Optional: Add badge for Positions or Orders */}
              {(positions.length > 0 || openOrders.length > 0) && (
                <BadgeCount>{positions.length + openOrders.length}</BadgeCount> 
              )}
            </Tab>
            <Tab active={activeTab === 'presets'} onClick={() => setActiveTab('presets')}>
              preSet MGMT
            </Tab>
            <Tab active={activeTab === 'classical'} onClick={() => setActiveTab('classical')}>
              Classical
            </Tab>
          </TabContainer>
          
          <ContentArea>
            {renderTabContent()}
            
            {/* TradeSet Edit Modal */}
            {isEditModalOpen && selectedTradeSet && (
              <EditTradeSetModal
                isOpen={isEditModalOpen}
                tradeSet={selectedTradeSet}
                onClose={handleCloseEditModal}
                onTradeSetUpdated={handleTradeSetUpdated}
              />
            )}


            
            {/* Keep legacy Wizard Modal for backward compatibility if needed */}
            {isWizardModalOpen && (
              <Modal>
                <ModalContent>
                  <ModalHeader>
                    <ModalTitle>Create New PreSet</ModalTitle>
                    <CloseButton onClick={handleCloseWizardModal}>&times;</CloseButton>
                  </ModalHeader>
                  <Wizard 
                    onPresetCreated={handlePresetCreated}
                    selectedSymbol={selectedSymbol}
                    isInModal={true}
                  />
                </ModalContent>
              </Modal>
            )}
          </ContentArea>
        </>
      ) : isEditingPreset && editingPresetData ? (
        // Inline Preset Edit Wizard
        <InlinePanelContent>
          <InlinePanelHeader>
            <InlinePanelTitle>Edit PreSet: {editingPresetData.name}</InlinePanelTitle>
            <BackButton onClick={() => handleExitPresetEdit()}>
              Back to PreSet MGMT
            </BackButton>
          </InlinePanelHeader>
          
          <Wizard 
            onPresetCreated={handlePresetUpdated}
            selectedSymbol={editingPresetData.symbol}
            isInModal={false}
            editingPreset={editingPresetData}
          />
        </InlinePanelContent>
      ) : isCreatingTradeSet ? (
        // Inline TradeSet Creation Wizard
        <InlinePanelContent>
          <InlinePanelHeader>
            <InlinePanelTitle>Create New TradeSet</InlinePanelTitle>
            <BackButton onClick={() => handleExitPresetCreation()}>
              Back to PreSet MGMT
            </BackButton>
          </InlinePanelHeader>
          <Wizard 
            onPresetCreated={(anyObj: any) => {
              // Treat as tradeSet creation
              fetchTradeSets();
              toast.success(`TradeSet "${anyObj?.name || 'New TradeSet'}" created successfully`);
              // Exit and restore panel size
              setIsCreatingTradeSet(false);
              handleExitPresetCreation();
            }}
            selectedSymbol={selectedSymbol}
            isInModal={false}
            initialPresetType={'tradeset'}
          />
        </InlinePanelContent>
      ) : (
        // Inline Preset Creation Wizard
        <InlinePanelContent>
          <InlinePanelHeader>
            <InlinePanelTitle>Create New PreSet</InlinePanelTitle>
            <BackButton onClick={() => handleExitPresetCreation()}>
              Back to PreSet MGMT
            </BackButton>
          </InlinePanelHeader>
          
          <Wizard 
            onPresetCreated={handlePresetCreated}
            selectedSymbol={selectedSymbol}
            isInModal={false}
          />
        </InlinePanelContent>
      )}
    </PanelContainer>
  );
};

export default TradePanelComponent;

// Helper function (optional, could be moved to utils)
const formatTimestamp = (timestamp: number): string => {
  if (!timestamp) return '-';
  // Assuming timestamp is in milliseconds
  return new Date(timestamp).toLocaleString(); 
}; 